import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "@/utils/index";
import {
  SKILL_LEVEL_NAMING_STATE_OPTIONS,
  SKILL_LEVEL_REFERENCE_ROWS,
  SkillLevelNamingState,
  SkillLevelReferenceRow,
  normalizeSkillLevelNamingState,
  resolveSkillLevelDisplayLabel,
} from "@/constants/skillLevelStateNaming";
import { setSkillLevelNamingState } from "@/utils/skillLevelNamingRuntime";

type SkillLevelNamingContextValue = {
  namingState: SkillLevelNamingState;
  loading: boolean;
  referenceRows: SkillLevelReferenceRow[];
  stateOptions: { value: SkillLevelNamingState; label: string }[];
  setNamingState: (state: SkillLevelNamingState) => Promise<void>;
  refresh: () => Promise<void>;
  labelForSkill: (
    skillId?: string | null,
    canonicalName?: string | null,
    options?: { minimum?: boolean },
  ) => string;
};

const SkillLevelNamingContext = createContext<SkillLevelNamingContextValue | null>(
  null,
);

export function SkillLevelNamingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [namingState, setNamingStateLocal] =
    useState<SkillLevelNamingState>("DEFAULT");
  const [loading, setLoading] = useState(true);
  const [referenceRows, setReferenceRows] = useState<SkillLevelReferenceRow[]>(
    SKILL_LEVEL_REFERENCE_ROWS,
  );
  const [stateOptions, setStateOptions] = useState(
    SKILL_LEVEL_NAMING_STATE_OPTIONS,
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [accountRes, referenceRes] = await Promise.allSettled([
        axios.get("/v1/hospital/account/account-info-get"),
        axios.get("/v1/hospital/account/skill-level-naming-reference"),
      ]);

      if (
        accountRes.status === "fulfilled" &&
        accountRes.value?.data?.code === 200
      ) {
        const account = accountRes.value.data.data;
        setNamingStateLocal(
          normalizeSkillLevelNamingState(account?.skillLevelNamingState),
        );
        setSkillLevelNamingState(
          normalizeSkillLevelNamingState(account?.skillLevelNamingState),
        );
      }

      if (
        referenceRes.status === "fulfilled" &&
        referenceRes.value?.data?.code === 200
      ) {
        const body = referenceRes.value.data.data || {};
        if (Array.isArray(body.rows) && body.rows.length > 0) {
          setReferenceRows(body.rows);
        }
        if (Array.isArray(body.stateOptions) && body.stateOptions.length > 0) {
          setStateOptions(
            body.stateOptions.map((opt: any) => ({
              value: normalizeSkillLevelNamingState(opt.value),
              label: String(opt.label || opt.value),
            })),
          );
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setNamingState = useCallback(async (state: SkillLevelNamingState) => {
    const response = await axios.put(
      `/v1/hospital/account/skill-level-naming-state/${state}`,
    );
    if (response?.data?.code !== 200) {
      throw new Error(
        response?.data?.message || "Failed to update skill level naming state",
      );
    }
    setNamingStateLocal(state);
    setSkillLevelNamingState(state);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("sys_list_v3_skill");
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("skill-level-naming-changed", {
          detail: { namingState: state },
        }),
      );
    }
  }, []);

  const labelForSkill = useCallback(
    (
      skillId?: string | null,
      canonicalName?: string | null,
      options?: { minimum?: boolean },
    ) =>
      resolveSkillLevelDisplayLabel({
        skillId,
        canonicalName,
        namingState,
        minimum: options?.minimum ?? true,
      }),
    [namingState],
  );

  const value = useMemo(
    () => ({
      namingState,
      loading,
      referenceRows,
      stateOptions,
      setNamingState,
      refresh,
      labelForSkill,
    }),
    [
      namingState,
      loading,
      referenceRows,
      stateOptions,
      setNamingState,
      refresh,
      labelForSkill,
    ],
  );

  return (
    <SkillLevelNamingContext.Provider value={value}>
      {children}
    </SkillLevelNamingContext.Provider>
  );
}

export function useSkillLevelNaming() {
  const ctx = useContext(SkillLevelNamingContext);
  if (!ctx) {
    throw new Error(
      "useSkillLevelNaming must be used within SkillLevelNamingProvider",
    );
  }
  return ctx;
}

/** Safe hook for leaf components — returns defaults when provider is absent. */
export function useSkillLevelNamingOptional() {
  return useContext(SkillLevelNamingContext);
}
